import * as THREE from 'three';
import GUI from 'lil-gui';

const props = {
  canvasId: 'js-canvasGradient',
  guiFrameId: '',
  colors: ['#A3C3F8', '#167AE7', '#1630c6', '#66A4F5', '#687df4'],
} as const;

const state = {
  canvasEl: null as HTMLCanvasElement | null,
  guiFrameEl: null as HTMLElement | null,

  colors: [] as string[],
  scene: new THREE.Scene(),
  camera: new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1),
  renderer: null as THREE.WebGLRenderer | null,
  mesh: null as THREE.Mesh | null,
  material: null as THREE.ShaderMaterial | null,
  time: 0,
  gui: undefined as GUI | undefined,

  // クリーンアップ用
  animationFrameId: null as number | null,
  resizeHandler: null as (() => void) | null,

  isInView: false, // 追加：要素が表示されているかのフラグ
  observer: null as IntersectionObserver | null, // 追加：IntersectionObserver インスタンス
};

// Simplex Noise
const noiseShader = `
    vec3 mod289(vec3 x) {
        return x - floor(x * (1.0 / 289.0)) * 289.0;
    }

    vec4 mod289(vec4 x) {
        return x - floor(x * (1.0 / 289.0)) * 289.0;
    }

    vec4 permute(vec4 x) {
        return mod289(((x*34.0)+1.0)*x);
    }

    vec4 taylorInvSqrt(vec4 r) {
        return 1.79284291400159 - 0.85373472095314 * r;
    }

    float snoise(vec3 v) {
        const vec2 C = vec2(1.0/6.0, 1.0/3.0);
        const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);

        vec3 i  = floor(v + dot(v, C.yyy));
        vec3 x0 = v - i + dot(i, C.xxx);

        vec3 g = step(x0.yzx, x0.xyz);
        vec3 l = 1.0 - g;
        vec3 i1 = min(g.xyz, l.zxy);
        vec3 i2 = max(g.xyz, l.zxy);

        vec3 x1 = x0 - i1 + C.xxx;
        vec3 x2 = x0 - i2 + C.yyy;
        vec3 x3 = x0 - D.yyy;

        i = mod289(i);
        vec4 p = permute(permute(permute(
            i.z + vec4(0.0, i1.z, i2.z, 1.0))
            + i.y + vec4(0.0, i1.y, i2.y, 1.0))
            + i.x + vec4(0.0, i1.x, i2.x, 1.0));

        float n_ = 0.142857142857;
        vec3 ns = n_ * D.wyz - D.xzx;

        vec4 j = p - 49.0 * floor(p * ns.z * ns.z);

        vec4 x_ = floor(j * ns.z);
        vec4 y_ = floor(j - 7.0 * x_);

        vec4 x = x_ * ns.x + ns.yyyy;
        vec4 y = y_ * ns.x + ns.yyyy;
        vec4 h = 1.0 - abs(x) - abs(y);

        vec4 b0 = vec4(x.xy, y.xy);
        vec4 b1 = vec4(x.zw, y.zw);

        vec4 s0 = floor(b0) * 2.0 + 1.0;
        vec4 s1 = floor(b1) * 2.0 + 1.0;
        vec4 sh = -step(h, vec4(0.0));

        vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
        vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;

        vec3 p0 = vec3(a0.xy, h.x);
        vec3 p1 = vec3(a0.zw, h.y);
        vec3 p2 = vec3(a1.xy, h.z);
        vec3 p3 = vec3(a1.zw, h.w);

        vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
        p0 *= norm.x;
        p1 *= norm.y;
        p2 *= norm.z;
        p3 *= norm.w;

        vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
        m = m * m;
        return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
    }
`;

// ブレンド関数
const blendShader = `
    vec3 blendNormal(vec3 base, vec3 blend) {
        return blend;
    }

    vec3 blendNormal(vec3 base, vec3 blend, float opacity) {
        return (blendNormal(base, blend) * opacity + base * (1.0 - opacity));
    }
`;

const vertexShader = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position, 1.0);
  }
`;

const fragmentShader = `
  precision highp float;
  uniform float u_time;
  uniform vec2 u_resolution;
  uniform vec3 u_baseColor;
  uniform vec3 u_color1;
  uniform vec3 u_color2;
  uniform vec3 u_color3;
  uniform vec3 u_color4;

  ${noiseShader}
  ${blendShader}

  varying vec2 vUv;
  void main() {
    vec2 uv = gl_FragCoord.xy / u_resolution;
    float time = u_time * 0.000005;
    vec2 noiseCoord = u_resolution * uv * vec2(0.00014, 0.00029);
    vec3 color = u_baseColor;

    float noise1 = smoothstep(0.1, 0.63, snoise(vec3(noiseCoord.x * 2.0 + time * 3.0, noiseCoord.y * 3.0, time * 11.0 + 5.0))/2.0 + 0.5);
    color = blendNormal(color, u_color1, pow(noise1, 4.0));
    float noise2 = smoothstep(0.1, 0.70, snoise(vec3(noiseCoord.x * 2.5 + time * 3.3, noiseCoord.y * 3.5, time * 11.3 + 15.0))/2.0 + 0.5);
    color = blendNormal(color, u_color2, pow(noise2, 4.0));
    float noise3 = smoothstep(0.1, 0.77, snoise(vec3(noiseCoord.x * 3.0 + time * 3.6, noiseCoord.y * 4.0, time * 11.6 + 25.0))/2.0 + 0.5);
    color = blendNormal(color, u_color3, pow(noise3, 4.0));
    float noise4 = smoothstep(0.1, 0.84, snoise(vec3(noiseCoord.x * 3.5 + time * 3.9, noiseCoord.y * 4.5, time * 11.9 + 35.0))/2.0 + 0.5);
    color = blendNormal(color, u_color4, pow(noise4, 4.0));

    gl_FragColor = vec4(color, 1.0);
  }
`;

export const contactGradient = {
  init() {
    const canvasEl = document.getElementById(props.canvasId);
    if (!(canvasEl instanceof HTMLCanvasElement)) return;

    state.canvasEl = canvasEl;
    state.colors = [...props.colors];

    // IntersectionObserver の設定を追加
    this.setupIntersectionObserver();

    state.time = 0;

    state.scene = new THREE.Scene();
    state.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    state.renderer = new THREE.WebGLRenderer({
      canvas: state.canvasEl,
      antialias: true,
    });

    this.setMesh();

    // resizeハンドラーを保存
    state.resizeHandler = () => this.onResize();
    window.addEventListener('resize', state.resizeHandler);
    this.onResize();

    this.animate();

    const guiFrameEl = document.getElementById(props.guiFrameId);
    if (guiFrameEl instanceof HTMLElement) {
      state.guiFrameEl = guiFrameEl;
      this.setGUI();
    }
  },

  setGUI() {
    if (!state.guiFrameEl) return;

    state.gui = new GUI({ container: state.guiFrameEl });
    state.colors.forEach((color, i) => {
      const key = i.toString();
      state.gui!.addColor({ [key]: color }, key).onChange((value: string) => {
        state.colors[i] = value;
        const uniformKeys = ['u_baseColor', 'u_color1', 'u_color2', 'u_color3', 'u_color4'];
        if (state.material) {
          state.material.uniforms[uniformKeys[i]].value.set(value);
        }
      });
    });
  },

  setMesh() {
    const geometry = new THREE.PlaneGeometry(2, 2);

    state.material = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms: {
        u_time: { value: 0 },
        u_resolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
        u_baseColor: { value: new THREE.Color(state.colors[0]) },
        u_color1: { value: new THREE.Color(state.colors[1]) },
        u_color2: { value: new THREE.Color(state.colors[2]) },
        u_color3: { value: new THREE.Color(state.colors[3]) },
        u_color4: { value: new THREE.Color(state.colors[4]) },
      },
    });

    state.mesh = new THREE.Mesh(geometry, state.material);
    state.scene.add(state.mesh);
  },

  onResize() {
    if (!state.renderer || !state.material || !(state.canvasEl instanceof HTMLCanvasElement)) return;

    const { clientWidth: w, clientHeight: h } = state.canvasEl;
    state.renderer.setSize(w, h);
    state.renderer.setPixelRatio(window.devicePixelRatio);

    state.material.uniforms.u_resolution.value.set(w, h);
  },

  // IntersectionObserver のセットアップメソッドを追加
  setupIntersectionObserver() {
    if (!state.canvasEl) return;

    state.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          state.isInView = entry.isIntersecting;

          if (entry.isIntersecting) {
            // 画面内に入った時にアニメーションを開始
            // console.log('🟢 アニメーション開始: Contact Gradient 要素が表示領域に入りました');
            if (state.animationFrameId === null) {
              this.animate();
            }
          } else {
            // 画面外に出た時にアニメーションを停止
            // console.log('🔴 アニメーション停止: Contact Gradient 要素が表示領域から出ました');
            if (state.animationFrameId !== null) {
              cancelAnimationFrame(state.animationFrameId);
              state.animationFrameId = null;
            }
          }
        });
      },
      {
        threshold: 0, // 少しでも画面内に入ったら検知
      },
    );

    state.observer.observe(state.canvasEl);
  },

  animate() {
    if (!state.renderer || !state.material) return;

    // 画面内の場合のみアニメーションを継続
    if (state.isInView) {
      state.time += 32;
      state.material.uniforms.u_time.value = state.time;

      state.renderer.render(state.scene, state.camera);
      state.animationFrameId = requestAnimationFrame(() => this.animate());
    }
  },

  reset() {
    // IntersectionObserver の解除を追加
    if (state.observer) {
      state.observer.disconnect();
      state.observer = null;
    }

    // アニメーションフレームを停止
    if (state.animationFrameId !== null) {
      cancelAnimationFrame(state.animationFrameId);
      state.animationFrameId = null;
    }

    // resizeイベントリスナーを削除
    if (state.resizeHandler) {
      window.removeEventListener('resize', state.resizeHandler);
      state.resizeHandler = null;
    }

    // GUI破棄
    state.gui?.destroy();
    state.gui = undefined;

    // メッシュとマテリアルを破棄
    if (state.mesh) {
      state.scene.remove(state.mesh);
      state.mesh.geometry.dispose();
      (state.mesh.material as THREE.Material).dispose();
      state.mesh = null;
    }

    // マテリアル破棄
    if (state.material) {
      state.material.dispose();
      state.material = null;
    }

    // レンダラー破棄
    if (state.renderer) {
      state.renderer.dispose();
      state.renderer = null;
    }

    // canvasクリア
    if (state.canvasEl instanceof HTMLCanvasElement) {
      state.canvasEl.width = state.canvasEl.height = 0;
    }
    state.canvasEl = null;
    state.guiFrameEl = null;
  },
};
